"use client"

import { useState, useEffect } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { X, ShoppingCart, User2Icon } from "lucide-react";
import { registerProjectIframe } from "../../API";
import { QuoteConfirmation } from "./quote";

interface Product {
  id: string
  name: string
  selected: boolean
  quantity: number
  price: number
}

interface ProductKit {
  id: string
  name: string
  image: string
  canvas: string
  svg: string
  products: Product[]
}

interface BudgetModalProps {
  isOpen: boolean
  onClose: () => void
}

export function BudgetModal({ isOpen, onClose }: BudgetModalProps) {
  const [kits, setKits] = useState<ProductKit[]>([]);
  const [showConfirmation, setShowConfirmation] = useState(false)

  useEffect(() => {
    const storedKits = localStorage.getItem("kits")
    if (storedKits) { setKits(JSON.parse(storedKits)) }
  }, [])

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    state: "",
    comments: "",
    order: "",
    list: ""
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    (window as { [key: string]: any })["setupCamera"]();

    const svg_json = JSON.stringify(
      (window as { [key: string]: any }).fabricCanvas.toJSON([
        "default",
        "id",
        "scaleX",
        "scaleY",
        "styles",
        "left",
        "top",
        "originX",
        "oCoords",
        "lockMovementX",
        "lockMovementY",
        "lockScalingX",
        "lockScalingY",
        "lockRotation",
        "lockSkewingX",
        "lockSkewingY",
        "selectable",
        "evented",
        "hoverCursor",
        "hasControls",
        "absolutePositioned"
      ])
    )
    const fileJson = new Blob([svg_json], { type: 'application/json' });

    //
    //SVG THUMB FILE
    const container = document.getElementById("viewer-3d") as any;
    const urlObjThumb2 = container.toDataURL("image/webp");

    //
    const svgString =
      "data:image/svg+xml;charset=utf-8," +
      encodeURIComponent(
        (window as { [key: string]: any }).fabricCanvas.toSVG({
          viewBox: {
            x: 0,
            y: 0,
            width: (window as { [key: string]: any }).fabricCanvas.getWidth(),
            height: (window as { [key: string]: any }).fabricCanvas.getHeight(),
          },
          width: (window as { [key: string]: any }).fabricCanvas.getWidth(),
          height: (window as { [key: string]: any }).fabricCanvas.getHeight(),
          suppressPreamble: false
        })
      );
    const linkSVG = document.createElement("a") as any;
    linkSVG.href = svgString;
    linkSVG.download = "image.svg";
    /*linkSVG.click()
    return;*/
    let responseSVG = await fetch(linkSVG);
    let dataSVG = await responseSVG.blob();
    let metadataSVG = {
      type: "image/svg+xml",
    };
    let fileSVG = new File([dataSVG], "test.svg", metadataSVG);

    const listaPedidos = formData.comments
      .split('\n')              // separa por linhas
      .map(line => line.trim()) // remove espaços extras
      .filter(line => line);    // remove linhas vazias

    const newFormData = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      city: formData.city,
      state: formData.state,
      comments: formData.comments,
      list: formData.list
    }

    const data = await registerProjectIframe(
      newFormData,
      (window as { [key: string]: any }).project_iframe.user_id,
      fileJson,
      urlObjThumb2,
      fileSVG,
      (window as { [key: string]: any }).project_iframe.sport_id,
      (window as { [key: string]: any }).order
    ) as any;

    const phoneNumber = (window as { [key: string]: any }).seller_number;
    let limpo = phoneNumber.replace(/[()\-\s]/g, '');

    const message = "Olá! Estou interessado nesse modelo:\n\n" +
      "Ordem de Pedido: " + data.id + "\n\n" +
      //"Preview: https://nfghuenhuaecfyccyaib.supabase.co/storage/v1/object/public/images/" + data.thumb + "\n\n" +
      "Meus Dados: " + "\n\n" +
      "Nome: " + formData.name + "\n\n" +
      "Número: " + formData.phone + "\n\n" +
      "Email: " + formData.email + "\n\n" +
      "Cidade: " + formData.city + "\n\n" +
      "Observações: \n" + formData.comments + "\n\n" +
      "Lista de Pedidos: \n" + formData.list + "\n\n";

    setFormData({ ...formData, order: data.id })

    // Create the WhatsApp URL
    const url = `https://wa.me/${limpo}?text=${encodeURIComponent(message)}`;

    // Open the WhatsApp chat in a new tab or window
    window.open(url, '_blank');

    setFormData({ ...formData, order: data.id })

    setShowConfirmation(true);
  }

  (window as { [key: string]: any }).addKit = async (
    name: any,
    image: any,
    canvas: any,
    svg: any,
    products: any
  ) => {

    const newKit: ProductKit = {
      id: Date.now().toString(),
      name,
      image,
      canvas,
      svg,
      products,
    }

    // Recupera kits já salvos (se existirem)
    const storedKits = JSON.parse(localStorage.getItem("kits") || "[]")

    // Adiciona o novo ao array existente
    const updatedKits = [...storedKits, newKit]

    // Atualiza o estado React
    setKits(updatedKits)
  }

  if (!isOpen) return null

  const isFormValid =
    formData.name.trim() !== "" &&
    formData.phone.trim() !== "";

  const handleDownloadPDF = () => { (window as { [key: string]: any })["generatePDF"](); }

  // Adicione isso dentro do seu componente ou no window global
  (window as any).setClientName = (newName: string) => {
    setFormData((prev) => ({ ...prev, name: newName }));
  };

  (window as any).setClientPhone = (newPhone: string) => {
    setFormData((prev) => ({ ...prev, phone: newPhone }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000000] p-4">

      {showConfirmation && (
        <div className="z-[10000000000000000000000000] relative">
          <QuoteConfirmation
            onClose={() => setShowConfirmation(false)}
            onDownloadPDF={handleDownloadPDF}
            customerName={formData.name.trim()}

            customerPhone={formData.phone.trim()}
            customerEmail={formData.email.trim()}
            customerCity={formData.city.trim()}
            customerDesc={formData.comments.trim()}
          />
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-x-hidden overflow-x-hidden overflow-y-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShoppingCart className="w-6 h-6" />
            <h2 className="text-2xl font-bold">SUA LISTA DE ORÇAMENTO</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-white hover:bg-white/20 rounded-full p-2">
            <X className="w-6 h-6" />
          </Button>
        </div>

        <div className="overflow-y-auto" id="budget-scroll">
          <div className="overflow-y-auto w-full">
            <div className="p-2 lg:p-6 space-y-6">
              {/* Product Kits */}
              {kits.map((kit) => (
                <div key={kit.id} className="bg-gray-50 rounded-xl lg:p-6 p-2 border border-gray-200">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-1">
                      <img
                        id="img-budget"
                        src={kit.image || "/placeholder.svg"}
                        alt={kit.name}
                        className="w-full h-auto object-cover rounded-lg border-2 border-blue-200 h-auto"
                      />
                      {/**<div className="flex justify-center w-full">
                        <Button id="download-iframe" className="bg-[green] text-white hover:bg-[orange]">Baixar Preview</Button>
                      </div> */}
                    </div>
                    <div className="space-y-4">
                      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200">
                        <div className="flex items-center gap-3 mb-6">
                          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                            <span className="text-white text-sm">💬</span>
                          </div>
                          <h3 className="text-xl font-semibold text-gray-800">DADOS DE CONTATO</h3>
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-4 text-black">
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <Input
                                placeholder="Nome:"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                className="border-gray-300 focus:border-blue-500"
                                required
                              />
                            </div>
                            <div>
                              <Input
                                type="email"
                                placeholder="Email:"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                className="border-gray-300 focus:border-blue-500"
                                
                              />
                            </div>
                          </div>
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <Input
                                placeholder="Celular: (00)0000-00000"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                className="border-gray-300 focus:border-blue-500"
                                required
                              />
                            </div>
                            <div>
                              <Input
                                placeholder="Cidade:"
                                value={formData.city}
                                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                                className="border-gray-300 focus:border-blue-500"
                                
                              />
                            </div>
                          </div>
                          <div>
                            <Textarea
                              placeholder="Observações:"
                              value={formData.comments}
                              onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                              className="border-gray-300 focus:border-blue-500 min-h-[100px]"
                              rows={4}
                            />
                          </div>
                          <div>
                            <Textarea
                              placeholder="Lista de Pedidos:"
                              value={formData.list}
                              onChange={(e) => setFormData({ ...formData, list: e.target.value })}
                              className="border-gray-300 focus:border-blue-500 min-h-[100px]"
                              rows={4}
                            />
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Form */}
          <div className="p-2 lg:p-6 space-y-6 !pt-0 text-black">
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                  <User2Icon className="text-white"></User2Icon>
                </div>
                <h3 className="text-xl font-semibold text-gray-800">VENDEDORES</h3>
              </div>
              {!isFormValid && (
                <p className="text-sm text-red-500 mt-2">
                  Preencha os dados para contatar os vendedores
                </p>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className={`
    text-center pt-4 pb-4 flex overflow-x-auto w-full h-[220px]
    transition-opacity
    ${!isFormValid ? "opacity-40 pointer-events-none select-none" : ""}
  `} id="list-sellers"></div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}